import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { useAuthStore } from './store/authStore';
import { darkOrangeTheme } from './config/theme';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Screens from './pages/Screens';
import Clients from './pages/Clients';
import Videos from './pages/Videos';
import Playlists from './pages/Playlists';
import Schedules from './pages/Schedules';
import Reports from './pages/Reports';
import PlaybackReport from './pages/PlaybackReport';
import Layout from './components/Layout';

function ProtectedRoute({ children, blockRoles = [] }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);

  useEffect(() => {
    if (user && user.role === 'client') {
      logout();
    }
  }, [user, logout]);

  if (!isAuthenticated || (user && user.role === 'client')) {
    return <Navigate to="/login" replace />;
  }

  if (user && blockRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
}

export default function App() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const fetchUser = useAuthStore((state) => state.fetchUser);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUser().catch(() => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      });
    }
  }, [isAuthenticated, fetchUser]);

  return (
    <ConfigProvider theme={darkOrangeTheme}>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/screens"
            element={
              <ProtectedRoute>
                <Screens />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/clients"
            element={
              <ProtectedRoute blockRoles={['sub_admin']}>
                <Clients />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/videos"
            element={
              <ProtectedRoute>
                <Videos />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/playlists"
            element={
              <ProtectedRoute>
                <Playlists />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/schedules"
            element={
              <ProtectedRoute>
                <Schedules />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/reports"
            element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/playback-report"
            element={
              <ProtectedRoute>
                <PlaybackReport />
              </ProtectedRoute>
            }
          />

          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Router>
    </ConfigProvider>
  );
}
